import { DateRange } from "react-day-picker";

import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";

interface OutstandingPayment {
  id: string;
  total_amount?: number;
  balance?: number;
  payment_due_date: string;
  client: { name: string; tax_id: string };
  daysRemaining: number;
  paid_amount: number;
  remaining_amount: number;
  payments?: any[];
  source: "sales_request" | "manual_facturas";
  doc_number?: string;
  doc_type?: string;
}

export const useOutstandingPayments = (dateRange?: DateRange) => {
  // Fetch outstanding payments from both sales_request and manual_facturas tables
  const { data: outstandingPayments, isLoading: isLoadingOutstandingPayments } =
    useQuery({
      queryKey: [
        "outstanding-payments",
        dateRange?.from?.toISOString(),
        dateRange?.to?.toISOString(),
      ],
      queryFn: async () => {
        try {
          const today = new Date();

          // Query sales_request for approved orders that are not paid
          let salesRequestQuery = supabase
            .from("sales_request")
            .select("*, client:clients(name, tax_id)")
            .not("status", "in", "(test,rejected)")
            .eq("paid", false);

          // Apply date range filter if provided (filter by payment_due_date)
          if (dateRange?.from) {
            const fromDate = new Date(dateRange.from);
            fromDate.setHours(0, 0, 0, 0);
            salesRequestQuery = salesRequestQuery.gte(
              "payment_due_date",
              fromDate.toISOString()
            );

            if (dateRange.to) {
              const toDate = new Date(dateRange.to);
              toDate.setHours(23, 59, 59, 999);
              salesRequestQuery = salesRequestQuery.lte(
                "payment_due_date",
                toDate.toISOString()
              );
            }
          }

          const { data: salesRequestData, error: salesRequestError } =
            await salesRequestQuery;

          if (salesRequestError) {
            console.error(
              "Error fetching outstanding sales requests:",
              salesRequestError
            );
          }

          // Query manual_facturas for unpaid balances
          let manualFacturasQuery = supabase
            .from("manual_facturas")
            .select(
              `
              *,
              clients!inner (
                name,
                tax_id
              )
            `
            )
            .gt("balance", 0);

          // Apply date range filter if provided (filter by payment_due_date)
          if (dateRange?.from) {
            const fromDate = new Date(dateRange.from);
            fromDate.setHours(0, 0, 0, 0);
            manualFacturasQuery = manualFacturasQuery.gte(
              "payment_due_date",
              fromDate.toISOString()
            );

            if (dateRange.to) {
              const toDate = new Date(dateRange.to);
              toDate.setHours(23, 59, 59, 999);
              manualFacturasQuery = manualFacturasQuery.lte(
                "payment_due_date",
                toDate.toISOString()
              );
            }
          }

          const { data: manualFacturasData, error: manualFacturasError } =
            await manualFacturasQuery;

          if (manualFacturasError) {
            console.error(
              "Error fetching outstanding manual facturas:",
              manualFacturasError
            );
          }

          const allOutstandingPayments: OutstandingPayment[] = [];

          // Process sales_request data
          if (salesRequestData) {
            const processedSalesRequests = await Promise.all(
              salesRequestData.map(async (payment) => {
                const dueDate = new Date(payment.payment_due_date);
                const timeDiff = dueDate.getTime() - today.getTime();
                const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

                // Use the SQL function to get the paid amount
                const { data: paidAmountData, error: paidAmountError } =
                  await supabase.rpc("get_sales_request_paid_amount", {
                    sales_request_id: payment.id,
                  });

                if (paidAmountError) {
                  console.error("Error fetching paid amount:", paidAmountError);
                }

                const paidAmount = paidAmountData || 0;

                // Fetch all payments for this sales request
                const { data: paymentsData, error: paymentsError } =
                  await supabase
                    .from("payments")
                    .select("*")
                    .eq("sales_request", payment.id);

                if (paymentsError) {
                  console.error(
                    "Error fetching payments for sales request:",
                    paymentsError
                  );
                }

                // Calculate the remaining amount after accounting for partial payments
                const remainingAmount =
                  Number(payment.total_amount) - Number(paidAmount);

                return {
                  ...payment,
                  daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
                  paid_amount: paidAmount,
                  remaining_amount: remainingAmount > 0 ? remainingAmount : 0,
                  payments: paymentsData || [],
                  source: "sales_request" as const,
                };
              })
            );

            // Filter to only show payments that are not late (due date is in the future)
            const futureSalesRequests = processedSalesRequests.filter(
              (payment) => {
                const dueDate = new Date(payment.payment_due_date);
                return dueDate > today;
              }
            );

            allOutstandingPayments.push(...futureSalesRequests);
          }

          // Process manual_facturas data
          if (manualFacturasData) {
            const processedManualFacturas = manualFacturasData
              .filter((factura) => {
                // Only include facturas that are not late (due date is in the future)
                if (!factura.payment_due_date) return true; // Include if no due date
                const dueDate = new Date(factura.payment_due_date);
                return dueDate > today;
              })
              .map((factura) => {
                const dueDate = factura.payment_due_date
                  ? new Date(factura.payment_due_date)
                  : new Date();
                const timeDiff = dueDate.getTime() - today.getTime();
                const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

                return {
                  id: factura.id,
                  total_amount: factura.dues || factura.balance || 0,
                  balance: factura.balance || 0,
                  payment_due_date:
                    factura.payment_due_date || new Date().toISOString(),
                  client: {
                    name:
                      (factura.clients as any)?.name ||
                      factura.client_name ||
                      "",
                    tax_id:
                      (factura.clients as any)?.tax_id ||
                      factura.client_tax_id ||
                      "",
                  },
                  daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
                  paid_amount: factura.paid || 0,
                  remaining_amount: factura.balance || 0,
                  payments: [],
                  source: "manual_facturas" as const,
                  doc_number: factura.doc_number,
                  doc_type: factura.doc_type,
                };
              });

            allOutstandingPayments.push(...processedManualFacturas);
          }

          return allOutstandingPayments;
        } catch (error) {
          console.error("Error in outstanding payments query:", error);
          return [];
        }
      },
    });

  // Calculate total value of outstanding payments, accounting for partial payments
  const totalOutstandingPayments = outstandingPayments
    ? outstandingPayments.reduce((sum, payment) => {
        return sum + (payment.remaining_amount || 0);
      }, 0)
    : 0;

  return {
    outstandingPayments: outstandingPayments || [],
    isLoadingOutstandingPayments,
    totalOutstandingPayments,
  };
};
